import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui'
import { Braces, GitBranch } from 'lucide-react'
import { Fragment, useMemo, useRef, useState } from 'react'

import {
  type PreviewMedia,
  WhatsAppPreview,
} from '@/components/common/whatsapp-preview'
import {
  type MessageVariable,
  VARIABLE_GROUP_LABEL,
  type VariableGroup,
  variablesForGroups,
} from '@/lib/message-variables'

interface Props {
  value: string
  onChange: (value: string) => void
  /** grupos de variáveis disponíveis neste contexto */
  groups: VariableGroup[]
  /** variáveis extras (ex.: campos personalizados da organização) */
  customVariables?: MessageVariable[]
  rows?: number
  showPreview?: boolean
  /** mídia anexada, exibida no preview acima do texto */
  media?: PreviewMedia | null
  placeholder?: string
  id?: string
  className?: string
}

// Mesmas classes do <Textarea> de @repo/ui — usamos um textarea nativo aqui
// para ter acesso ao ref (inserção da variável na posição do cursor).
const TEXTAREA_CLASS =
  'border-input placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 flex field-sizing-content min-h-16 w-full rounded-md border bg-white px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm'

type ConditionOp = 'eq' | 'neq' | 'filled'

/**
 * Editor de mensagem padronizado: botões "Inserir variável" (menu agrupado) e
 * "Inserir condição" (construtor visual que monta o bloco {{#se ...}}), ambos
 * inserindo na posição do cursor, + textarea + preview estilo WhatsApp.
 */
export function MessageComposer({
  value,
  onChange,
  groups,
  customVariables = [],
  rows = 3,
  showPreview = true,
  media,
  placeholder,
  id,
  className,
}: Props) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Todas as variáveis disponíveis (canônicas dos grupos + personalizadas).
  const allVariables = useMemo<MessageVariable[]>(
    () => [...variablesForGroups(groups), ...customVariables],
    [groups, customVariables]
  )

  // Amostras dos campos personalizados para o preview resolver {{key}}.
  const extraSamples = useMemo(
    () =>
      Object.fromEntries(
        customVariables.map((v) => [v.key, v.sample])
      ) as Record<string, string>,
    [customVariables]
  )

  function insert(token: string) {
    const el = textareaRef.current
    if (!el) {
      onChange(value ? `${value}${token}` : token)
      return
    }
    const start = el.selectionStart ?? value.length
    const end = el.selectionEnd ?? value.length
    const next = value.slice(0, start) + token + value.slice(end)
    onChange(next)
    requestAnimationFrame(() => {
      el.focus()
      const caret = start + token.length
      el.setSelectionRange(caret, caret)
    })
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex flex-wrap justify-end gap-2">
        <ConditionBuilder variables={allVariables} onInsert={insert} />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
            >
              <Braces className="h-3.5 w-3.5" />
              Inserir variável
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="max-h-80 w-64 overflow-y-auto"
            onCloseAutoFocus={(event) => event.preventDefault()}
          >
            {groups.map((group, groupIndex) => {
              const groupVars = variablesForGroups([group])
              if (groupVars.length === 0) return null
              return (
                <Fragment key={group}>
                  {groupIndex > 0 ? <DropdownMenuSeparator /> : null}
                  <DropdownMenuLabel className="text-muted-foreground text-2xs font-semibold tracking-wide uppercase">
                    {VARIABLE_GROUP_LABEL[group]}
                  </DropdownMenuLabel>
                  {groupVars.map((variable) => (
                    <VariableItem
                      key={variable.key}
                      variable={variable}
                      onInsert={insert}
                    />
                  ))}
                </Fragment>
              )
            })}
            {customVariables.length > 0 ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-muted-foreground text-2xs font-semibold tracking-wide uppercase">
                  {VARIABLE_GROUP_LABEL.campo}
                </DropdownMenuLabel>
                {customVariables.map((variable) => (
                  <VariableItem
                    key={variable.key}
                    variable={variable}
                    onInsert={insert}
                  />
                ))}
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <textarea
        ref={textareaRef}
        id={id}
        rows={rows}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className={TEXTAREA_CLASS}
      />
      {showPreview ? (
        <WhatsAppPreview
          text={value}
          media={media}
          extraSamples={extraSamples}
        />
      ) : null}
    </div>
  )
}

function VariableItem({
  variable,
  onInsert,
}: {
  variable: MessageVariable
  onInsert: (token: string) => void
}) {
  return (
    <DropdownMenuItem
      onSelect={() => onInsert(`{{${variable.key}}}`)}
      className="flex items-center justify-between gap-3"
    >
      <span>{variable.label}</span>
      <span className="text-muted-foreground font-mono text-xs">
        {`{{${variable.key}}}`}
      </span>
    </DropdownMenuItem>
  )
}

/**
 * Construtor visual de condição: monta um bloco
 * {{#se VAR = "VALOR"}}texto{{senao}}texto2{{/se}} sem o usuário precisar
 * escrever a sintaxe à mão. Pensado para quem não é desenvolvedor.
 */
function ConditionBuilder({
  variables,
  onInsert,
}: {
  variables: MessageVariable[]
  onInsert: (token: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [variable, setVariable] = useState('')
  const [op, setOp] = useState<ConditionOp>('eq')
  const [compareValue, setCompareValue] = useState('')
  const [ifText, setIfText] = useState('')
  const [elseText, setElseText] = useState('')

  function reset() {
    setVariable('')
    setOp('eq')
    setCompareValue('')
    setIfText('')
    setElseText('')
  }

  const canInsert =
    variable.trim() !== '' &&
    ifText.trim() !== '' &&
    (op === 'filled' || compareValue.trim() !== '')

  function build() {
    const condition =
      op === 'filled'
        ? variable
        : `${variable} ${op === 'eq' ? '=' : '!='} "${compareValue}"`
    const elsePart = elseText ? `{{senao}}${elseText}` : ''
    return `{{#se ${condition}}}${ifText}${elsePart}{{/se}}`
  }

  function handleInsert() {
    onInsert(build())
    reset()
    setOpen(false)
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) reset()
      }}
    >
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="gap-1.5">
          <GitBranch className="h-3.5 w-3.5" />
          Inserir condição
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-3">
        <div className="space-y-1">
          <p className="text-sm font-medium">
            Mostrar texto conforme uma condição
          </p>
          <p className="text-muted-foreground text-xs">
            Ex.: se o responsável for “Camila”, mostrar “a”; senão, “o”.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Variável</Label>
          <Select value={variable || undefined} onValueChange={setVariable}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Escolha a variável" />
            </SelectTrigger>
            <SelectContent>
              {variables.map((v) => (
                <SelectItem key={v.key} value={v.key}>
                  {v.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Condição</Label>
          <Select value={op} onValueChange={(v) => setOp(v as ConditionOp)}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="eq">é igual a</SelectItem>
              <SelectItem value="neq">é diferente de</SelectItem>
              <SelectItem value="filled">está preenchido</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {op !== 'filled' ? (
          <div className="space-y-1.5">
            <Label className="text-xs">Valor</Label>
            <Input
              value={compareValue}
              onChange={(e) => setCompareValue(e.target.value)}
              placeholder="Ex.: Camila"
            />
          </div>
        ) : null}

        <div className="space-y-1.5">
          <Label className="text-xs">Mostrar este texto</Label>
          <Input
            value={ifText}
            onChange={(e) => setIfText(e.target.value)}
            placeholder="Ex.: a"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Senão, mostrar (opcional)</Label>
          <Input
            value={elseText}
            onChange={(e) => setElseText(e.target.value)}
            placeholder="Ex.: o"
          />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              reset()
              setOpen(false)
            }}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!canInsert}
            onClick={handleInsert}
          >
            Inserir
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
